import { action, applySnapshot, extend, register, type SnapshotIn, types } from "@gadgetinc/mobx-quick-tree";
import { BillingContract, DefaultNominalBillingContract } from "./BillingContract";
import { UsageThresholdStatusMSTType } from "./UsageThresholdStatus";
import { GadgetModelClass } from "./utils";

type BillingMstToProps<T> = Omit<SnapshotIn<T>, "key" | "type">;

export const BillingPaymentErrors = {
  STRIPE_PAYMENT_INTENT_NOT_FOUND: "STRIPE_PAYMENT_INTENT_NOT_FOUND",
  ORB_SUBSCRIPTION_CHANGE_NOT_FOUND: "ORB_SUBSCRIPTION_CHANGE_NOT_FOUND",
  ORB_NO_ACTIVE_SUBSCRIPTION: "ORB_NO_ACTIVE_SUBSCRIPTION",
  ORB_PAYMENT_ATTEMPT_NOT_FOUND: "ORB_PAYMENT_ATTEMPT_NOT_FOUND",
  ORB_PURCHASE_INVOICE_NOT_FOUND: "ORB_PURCHASE_INVOICE_NOT_FOUND",
  ORB_INVOICE_ALREADY_PAID: "ORB_INVOICE_ALREADY_PAID",
  ORB_INVOICE_UNEXPECTED_STATE: "ORB_INVOICE_UNEXPECTED_STATE",
  WAITING_FOR_PAYMENT: "WAITING_FOR_PAYMENT",
  STRIPE_PAYMENT_WAITING_ON_CUSTOMER_ACTION: "STRIPE_PAYMENT_WAITING_ON_CUSTOMER_ACTION",
  STRIPE_FAILED_TO_CREATE_PAYMENT_INTENT: "STRIPE_FAILED_TO_CREATE_PAYMENT_INTENT",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
} as const;

export const isBillingPaymentError = (error: string): error is BillingPaymentError => {
  return Object.values(BillingPaymentErrors).includes(error as BillingPaymentError);
};

export type BillingPaymentError = (typeof BillingPaymentErrors)[keyof typeof BillingPaymentErrors];

export type BillingPaymentResultSuccess = { status: "succeeded" };
export type BillingPaymentResultFailed = { status: "failed"; message: string };
export type BillingPaymentResultWaitingOnBankConfirmation = {
  status: "waiting_on_bank_confirmation";
  paymentIntentId: string;
  requiresCustomerApproval: boolean;
  completesAt: number;
};
export type BillingPaymentResultError = { status: "error"; error: BillingPaymentError; message?: string };
export type BillingPaymentResult =
  | BillingPaymentResultSuccess
  | BillingPaymentResultFailed
  | BillingPaymentResultWaitingOnBankConfirmation
  | BillingPaymentResultError;

export enum BillingAccountStatus {
  ENABLED = "enabled",
  PAUSED = "paused",
  FROZEN = "frozen",
  SUSPENDED = "suspended",
}

/** To pause the account when the spending limit is exceeded */
export type BillingAccountStatusBySpendingLimit = {
  type: "spending_limit";
  amountExceeded: number;
};

/** To resume the account */
export type BillingAccountStatusBySpendLimitIncreased = {
  type: "increased_spend_limit";
  newSpendLimit: number;
};

/** To pause the account when the usage limit is exceeded and the account is on the hobby plan */
export type BillingAccountStatusByUsageLimit = {
  type: "usage_limit";
};

export type BillingAccountStatusTriggeredBy = BillingAccountStatusBySpendingLimit | BillingAccountStatusByUsageLimit;

interface LineItemPropsBase {
  description: string;
  period: string;
  quantityNumeric: number;
  quantity: string;
  included?: string;
  unitPrice: string;
  unitPriceSize?: string;
  unitPriceSizeWithoutUnitAmount?: string;
  total: string;
  order: number;
  discount?: string;
}

export type FixedFeeLineItemProps = LineItemPropsBase & { isUsage: false };
export type UsageLineItemProps = LineItemPropsBase & {
  isUsage: true;
  usageStart: string;
  usageEnd: string;
  billableMetricId: string;
  priceId: string;
  priceName: string;
  subscriptionId: string;
  info?: string;
  includedAmountUsed: string;
  percentIncludedAmountUsed?: number;
  additionalUsage?: string;
  additionalUsageWithoutUnit?: string;
  numPackages?: number;
  unit?: string;
  quantityWithoutUnit: string;
  includedWithoutUnit: string;
  dailyUsageTarget: string;
};

export type LineItemProps = FixedFeeLineItemProps | UsageLineItemProps;

export const InvoicePaperSize = ["LETTER" as const, "A4" as const] as const;

export const MAX_SPEND_LIMIT_AMOUNT = 100_000_000;

@register
export class Invoice extends GadgetModelClass("Invoice", {
  id: types.string,
  isDraft: types.boolean,
  isVoid: types.boolean,
  isPaid: types.boolean,
  planName: types.maybe(types.string),
  isFreePlan: types.maybe(types.boolean),
  invoiceNumber: types.string,
  planFixedFeeCost: types.maybe(types.string),
  numberOfDeveloperSeats: types.number,
  developerSeatUnitCost: types.maybe(types.string),
  developerSeatsCost: types.string,
  numberOfAdditionalDeveloperSeats: types.number,
  additionalDeveloperSeatUnitCost: types.maybe(types.string),
  additionalDeveloperSeatsCost: types.string,
  numberOfAdditionalApplications: types.number,
  additionalApplicationsUnitCost: types.maybe(types.string),
  additionalApplicationsCost: types.string,
  usageCost: types.string,
  otherCost: types.maybe(types.string),
  invoiceSubTotal: types.maybe(types.string),
  discount: types.maybe(types.string),
  invoiceTotal: types.maybe(types.string),
  credit: types.maybe(types.string),
  amountDue: types.string,
  invoiceDate: types.string,
  invoiceDateLong: types.string,
  customerName: types.string,
  customerTaxId: types.maybe(types.string),
  billingAddress: types.maybe(types.array(types.string)),
  lineItems: types.frozen<LineItemProps[]>(),
  usagePeriod: types.maybe(types.string),
  usagePeriodStart: types.maybe(types.string),
  usagePeriodEnd: types.maybe(types.string),
  invoicePaperSize: types.enumeration(InvoicePaperSize),
}) {
  get amountDueNumeric() {
    return Number(this.amountDue.replace("$", ""));
  }
}

export type InvoiceProps = BillingMstToProps<typeof Invoice>;

@register
export class UpcomingInvoice extends extend(Invoice, {
  daysToIssue: types.number,
  upcomingCharge: types.string,
}) {}

export type UpcomingInvoiceProps = BillingMstToProps<typeof UpcomingInvoice>;

@register
export class BillingAccount extends GadgetModelClass("BillingAccount", {
  id: types.string,
  email: types.string,
  billingAccountStatus: types.enumeration(Object.values(BillingAccountStatus)),
  usageThresholdStatus: types.maybe(UsageThresholdStatusMSTType),
  billingContract: DefaultNominalBillingContract,
}) {
  @action
  setBillingContract(billingContract: SnapshotIn<typeof BillingContract>) {
    if (this.billingContract.key === billingContract.key) {
      applySnapshot(this.billingContract, billingContract);
    } else {
      this.billingContract = BillingContract.create(billingContract);
    }
  }

  @action
  setEmail(email: string) {
    this.email = email;
  }
}

export type BillingAccountProps = BillingMstToProps<typeof BillingAccount>;

export const MonthlyCadence = "monthly";
export const QuarterlyCadence = "quarterly";
export const AnnualCadence = "annual";
export const SemiAnnualCadence = "semi_annual";
export const OneTimeCadence = "one_time";
export const CustomCadence = "custom";
export const Cadences = [MonthlyCadence, QuarterlyCadence, AnnualCadence, SemiAnnualCadence, OneTimeCadence, CustomCadence] as const;
export type Cadence = (typeof Cadences)[number];

@register
export class BillingPlan extends GadgetModelClass("BillingPlan", {
  id: types.string,
  isEnterprise: types.boolean,
  isCustom: types.boolean,
  hasIncludedAmounts: types.boolean,
  name: types.string,
  maxUsers: types.maybeNull(types.number),
  isFreePlan: types.boolean,
  includedUsers: types.maybeNull(types.number),
  includedApplications: types.maybeNull(types.number),
  isSelectable: types.boolean,
  maxApplications: types.maybeNull(types.number),
  description: types.maybeNull(types.string),
  fixedFeeCadence: types.maybeNull(types.enumeration(Cadences)),
  fixedFeeMonthlyPriceNumeric: types.number,
  fixedFeeMonthlyPrice: types.string,
  perSeatPriceNumeric: types.number,
  perSeatPrice: types.string,
  additionalApplicationsPriceNumeric: types.number,
  additionalApplicationsPrice: types.string,
  logRetentionDays: types.maybeNull(types.number),
  hostingCosts: types.frozen<
    {
      hostingCost: string;
      included: string;
      unitPrice?: string;
      unitPriceSize?: string;
      unitPriceSizeWithoutUnitAmount?: string;
    }[]
  >(),
  currentPlan: types.maybe(types.boolean),
  currentPlanIsFreePlan: types.maybe(types.boolean),
  highlightedFeatures: types.frozen<string[]>(),
  newTeamCharges: types.frozen<ChargeOrCredit[]>(),
  newTeamTransactionAmount: types.string,
  aiCreditAllocationAmount: types.maybeNull(types.number),
  aiCreditAllocationCadence: types.maybeNull(types.enumeration(Cadences)),
  aiCreditTopUpSize: types.maybeNull(types.number),
  aiCreditTopUpCost: types.maybeNull(types.string),
}) {}

export type BillingPlanProps = BillingMstToProps<typeof BillingPlan>;

export type SpendLimitDetailsProps = {
  alertId: string;
  maxSpend: number | null;
  thresholds: number[];
  enabled: boolean;
};

export type CheckoutTransactionType = "credit" | "charge" | "none";

export interface ChargeOrCredit {
  description: string;
  amount: string;
  displayAmount?: string;
  highlightedFeature?: HighlightedFeature;
}

export type PlanChangeType = "upgrade" | "downgrade";

export interface HighlightedFeature {
  description: string;
}

export interface PurchaseDetailsProps {
  charges: ChargeOrCredit[];
  credits: ChargeOrCredit[];
  checkoutTransactionAmount: string;
  checkoutTransactionType: CheckoutTransactionType;
  costBasis: string;
  numberOfCredits: number;
}

export interface PlanChangeProps {
  subscriptionChangeId: string;
  newBillingPlanName: string;
  newBillingPlanIsFreePlan: boolean;
  newPerSeatPrice: string;
  newBillingCycleDayOfMonth: string;
  numberOfBillableUsers: number;
  planFixedFeeCadence?: Cadence | null;
  charges: ChargeOrCredit[];
  credits: ChargeOrCredit[];
  planChangeTransactionType: CheckoutTransactionType;
  planChangeTransactionAmount: string;
}

export interface BillableApplicationProps {
  id: string;
  slug: string;
}

export interface BillableUserProps {
  id: string;
  name: string | null;
  email: string;
}

@register
export class PaymentMethod extends GadgetModelClass("PaymentMethod", {
  id: types.string,
  last4: types.string,
  expiryMonth: types.string,
  expiryYear: types.string,
  brand: types.string,
  customerBillingCountry: types.maybeNull(types.maybe(types.string)),
}) {}

export type PaymentMethodProps = BillingMstToProps<typeof PaymentMethod>;

export interface Pagination {
  has_more: boolean;
  next_cursor: string | null;
}

export type ApplicationUsageProps = {
  metricName: string;
  quantity: string;
  percentageOfUsage?: string;
} & ({ applicationDeleted: false; application: BillableApplicationProps } | { applicationDeleted: true; applicationName: string });

type UsageReportDataPointRequiredKeys = "date" | "total";

export type UsageReportDataPoint = {
  date: string;
  total: number;
} & {
  [key in string]: key extends UsageReportDataPointRequiredKeys ? never : number;
};

export interface UsageReport {
  unit?: string;
  dailyUnit?: string;
  keys: string[];
  colors: string[];
  unitFactor: number;
  data: UsageReportDataPoint[];
  subTitle?: string;
  learnMoreLink?: string;
  limitTooltip?: string;
  lastSnapshot?: number;
  lastSnapshotLabel?: string;
  lastSnapshotToolTip?: string;
}
